/**
 * 이사 품목 스펙 도메인 타입
 * @see src/app/draft/moving-item-card-grid/page.tsx (Draft C)
 */

import type { RoomTypeValue } from '@/entities/visit-counsel/model/house-sketch.types';

export type AttrDefType = 'STRING' | 'NUMBER' | 'BOOLEAN' | 'ENUM';

export type RuleType =
  | 'ALWAYS_MATCH'
  | 'NEVER_MATCH'
  | 'NUMBER_GTE'
  | 'NUMBER_GT'
  | 'NUMBER_LTE'
  | 'NUMBER_LT'
  | 'BOOLEAN_EQ'
  | 'ENUM_IN'
  | 'AND'
  | 'CROSS_AND';

export type EffectType = 'GUIDE_MESSAGE' | 'OPTION_HINT' | 'SERVICE_TYPE_CONSTRAINT';

/** CROSS_AND: 다른 attribute key에 대한 하위 규칙 */
export interface AttributeClause {
  key: string;
  rule: ConditionRule;
}

export interface ConditionRule {
  type: RuleType;
  threshold?: number; // NUMBER_GTE/GT/LTE/LT
  value?: boolean; // BOOLEAN_EQ
  values?: string[]; // ENUM_IN
  /** AND: 동일 attribute에 적용할 하위 규칙 */
  rules?: ConditionRule[];
  /** CROSS_AND: 여러 attribute 간 복합 조건 */
  clauses?: AttributeClause[];
}

export interface ConditionEffect {
  type: EffectType;
  message?: string; // GUIDE_MESSAGE
  optionKey?: string; // OPTION_HINT
  label?: string; // OPTION_HINT
  allowedServiceSets?: string[]; // SERVICE_TYPE_CONSTRAINT (클라이언트 SERVICE_SET 이름)
  allowedServiceTypeKeys?: string[]; // SERVICE_TYPE_CONSTRAINT (서버 개별 서비스 TYPE 키 원본)
}

export interface AttributeCondition {
  rule: ConditionRule;
  effect: ConditionEffect;
  /** 편집기 전용: 규칙 행 React key 안정화 (API 저장 시 제거) */
  _clientRowKey?: string;
}

export interface AttrDef {
  key: string;
  name?: string;
  type: AttrDefType;
  values?: string[];
  enumDisplayNames?: Record<string, string>;
  conditions?: AttributeCondition[];
}

/**
 * 조건 목록에서 "스펙 공통(속성 무관)"을 가리키는 attrIndex 센티널.
 * 속성이 하나도 없는 품목도 이 위치에 항상 참 규칙을 걸 수 있다.
 */
export const SPEC_LEVEL_ATTR_INDEX = -1;

export const SPEC_LEVEL_ATTR_LABEL = '스펙 공통';

export interface ConditionEffects {
  guideMessages: string[];
  optionHints: { optionKey: string; label: string }[];
  allowedServiceSets: string[] | null;
}

export interface CompileResult {
  valid: boolean;
  errors?: {
    key: string;
    name?: string;
    message: string;
    allowedValues?: string[];
  }[];
  conditionEffects?: ConditionEffects;
  /**
   * compile(검증) 응답에 포함되면, 입력 속성값 기준으로 계산된 짐 한 줄 표시명.
   * 서버 필드명은 `resolvedDisplayName`·`displayName` 등으로 올 수 있음 — API 레이어에서 정규화.
   */
  resolvedDisplayName?: string | null;
}

/** 표시명 템플릿 규칙 한 줄 (가이드 조건과 동일한 ConditionRule) */
export interface DisplayTemplateRule {
  rule: ConditionRule;
  template: string;
  /** 편집기 전용: 행 React key 안정화 (API 저장 시 제거) */
  _clientRowKey?: string;
}

export interface Spec {
  id: string;
  name: string;
  description: string;
  /** Unstructured Resource 토큰. 생성/수정 시 서버에 전달 */
  iconToken?: string;
  /** 아이콘 다운로드 URL. 조회 시 서버에서 반환, img src로 사용 */
  iconDownloadUrl?: string;
  attributes: AttrDef[];
  /** 속성에 종속되지 않는 스펙 공통 조건. 속성 없이도 항상 참 규칙을 걸 때 사용 */
  conditions?: AttributeCondition[];
  /** 짐 한 줄 표시용 기본 템플릿 (선택). 플레이스홀더: {displayName}, {attr:키}, {attr:키:접미사} */
  displayNameTemplate?: string | null;
  /** 순서형 조건부 표시명. 비어 있으면 조건부 없음. 비어 있지 않으면 마지막 행은 ALWAYS_MATCH */
  displayTemplateRules?: DisplayTemplateRule[];
  /**
   * UI 섹션 구분용 방 타입 태그. 비어 있으면 공간 무관(기타) 품목.
   * @see RoomType enum (서버 JSON은 enum name 문자열)
   */
  roomTypes?: RoomTypeValue[];
}

export const TYPE_DISPLAY: Record<AttrDefType, string> = {
  STRING: '텍스트',
  NUMBER: '숫자',
  BOOLEAN: '참/거짓',
  ENUM: '옵션',
};
